// src/components/admin/Dashboard.js
import React, { useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import {
  LogOut,
  Home,
  User,
  FolderOpen,
  Image,
  Video,
  Menu,
  X,
} from "lucide-react";
import HeroEditor from "./editors/HeroEditor";
import AboutEditor from "./editors/AboutEditor";
import ProjectsEditor from "./editors/ProjectsEditor";

const Dashboard = () => {
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState("hero");
  const [sidebarOpen, setSidebarOpen] = useState(true);

  const handleLogout = async () => {
    try {
      await logout();
      navigate("/admin/login");
    } catch (error) {
      console.error("Error al cerrar sesión:", error);
    }
  };

  const sections = [
    { id: "hero", name: "Inicio / Hero", icon: Home },
    { id: "about", name: "Sobre Mí", icon: User },
    { id: "projects", name: "Proyectos", icon: FolderOpen },
  ];

  const renderEditor = () => {
    switch (activeSection) {
      case "hero":
        return <HeroEditor />;
      case "about":
        return <AboutEditor />;
      case "projects":
        return <ProjectsEditor />;
      default:
        return <HeroEditor />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar */}
      <aside
        className={`${
          sidebarOpen ? "w-64" : "w-0"
        } bg-gradient-to-b from-gray-900 to-gray-800 text-white transition-all duration-300 overflow-hidden fixed h-full z-50`}
      >
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-xl font-bold">Panel Admin</h2>
            <button
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden text-white hover:bg-gray-700 p-2 rounded-lg"
            >
              <X size={20} />
            </button>
          </div>

          {/* User info */}
          <div className="mb-8 pb-6 border-b border-gray-700">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center">
                <User size={20} />
              </div>
              <div>
                <p className="text-sm font-medium">Administrador</p>
                <p className="text-xs text-gray-400 truncate max-w-[150px]">
                  {currentUser?.email}
                </p>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <nav className="space-y-2">
            {sections.map((section) => {
              const IconComponent = section.icon;
              return (
                <button
                  key={section.id}
                  onClick={() => setActiveSection(section.id)}
                  className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-all ${
                    activeSection === section.id
                      ? "bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg"
                      : "text-gray-300 hover:bg-gray-700"
                  }`}
                >
                  <IconComponent size={20} />
                  <span className="font-medium">{section.name}</span>
                </button>
              );
            })}
          </nav>

          {/* Actions */}
          <div className="mt-8 pt-6 border-t border-gray-700 space-y-2">
            <button
              onClick={() => window.open("/", "_blank")}
              className="w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-gray-300 hover:bg-gray-700 transition-all"
            >
              <Image size={20} />
              <span>Ver sitio web</span>
            </button>
            <button
              onClick={handleLogout}
              className="w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-red-400 hover:bg-red-900/20 transition-all"
            >
              <LogOut size={20} />
              <span>Cerrar sesión</span>
            </button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <main
        className={`flex-1 ${sidebarOpen ? "ml-64" : "ml-0"} transition-all duration-300`}
      >
        {/* Top bar */}
        <header className="bg-white border-b border-gray-200 sticky top-0 z-40">
          <div className="px-6 py-4 flex items-center justify-between">
            <div className="flex items-center space-x-4">
              {!sidebarOpen && (
                <button
                  onClick={() => setSidebarOpen(true)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <Menu size={24} className="text-gray-600" />
                </button>
              )}
              <h1 className="text-2xl font-bold text-gray-800">
                {sections.find((s) => s.id === activeSection)?.name}
              </h1>
            </div>
            <div className="flex items-center space-x-4">
              <div className="hidden sm:block text-sm text-gray-600">
                Última actualización: {new Date().toLocaleDateString("es-ES")}
              </div>
            </div>
          </div>
        </header>

        {/* Content area */}
        <div className="p-6">{renderEditor()}</div>
      </main>

      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
};

export default Dashboard;
